import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Goal as GoalModel,
  IGetOrCreateGoalSegmentResult,
  IPredicate,
} from '@feathr/blackbox';
import { AlertV2 as Alert, EAlertV2Type, Radios } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import DonationFormsSelect from '../../DonationFormsSelect';
import { buildDonationFormSubmissionPredicates } from '../Goal.predicates';
import GoalDevConsole from '../GoalDevConsole';

import * as styles from './DonationFormSubmissionGoal.css';

type TFormSelection = 'any' | 'specific';

interface IProps {
  goal: GoalModel;
}

function DonationFormSubmissionGoal({ goal }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Segments } = useStore();
  const [selectMode, setSelectMode] = useState<TFormSelection>('any');
  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [currentPredicates, setCurrentPredicates] = useState<IPredicate[]>([]);
  const [segmentResult, setSegmentResult] = useState<IGetOrCreateGoalSegmentResult | undefined>(
    undefined,
  );
  const {
    spacing: { '4': gap },
  } = useMantineTheme();

  useEffect(() => {
    async function updateSegment(): Promise<void> {
      const formIds = selectMode === 'specific' ? selectedForms : undefined;
      const predicates = buildDonationFormSubmissionPredicates(formIds);
      const segmentName = `Donation Form Goal - ${goal.get('name') || 'Unnamed'}`;

      setCurrentPredicates(predicates);
      const result = await Segments.getOrCreateGoalSegment(predicates, segmentName);
      setSegmentResult(result);
      goal.set({ segment: result.segment.id });
    }

    if (selectMode === 'any' || (selectMode === 'specific' && selectedForms.length > 0)) {
      updateSegment();
    }
  }, [selectMode, selectedForms, Segments, goal]);

  function handleSelectMode(value?: string): void {
    if (!value) {
      return;
    }
    setSelectMode(value as TFormSelection);
  }

  const options = [
    { id: 'any', name: t('Any donation form submission') },
    { id: 'specific', name: t('Submissions on specific donation form(s)') },
  ];

  function handleFormSelection(value?: string[]): void {
    if (!value) {
      return;
    }
    setSelectedForms(value);
  }

  return (
    <Flex direction={'column'} gap={gap}>
      <Alert
        className={styles.alert}
        description={t(
          'The actual donation value submitted through the donation form will be used as the conversion value. You do not have the opportunity to set a custom conversion value.',
        )}
        title={t('Important')}
        type={EAlertV2Type.info}
      />
      <Radios
        helpText={t('Choose which donation form submissions will be tracked as conversions.')}
        label={t('Donation form selection')}
        layout={'block'}
        onChange={handleSelectMode}
        options={options}
        value={selectMode}
      />
      {selectMode === 'specific' && (
        <DonationFormsSelect
          filters={{ state: 'published' }}
          isMulti={true}
          label={t('Donation form(s)')}
          onChange={handleFormSelection}
          placeholder={t('Start typing to search donation forms')}
          value={selectedForms}
        />
      )}
      <GoalDevConsole predicates={currentPredicates} segmentResult={segmentResult} />
    </Flex>
  );
}

export default observer(DonationFormSubmissionGoal);
